import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { LifeBuoy, Hand, Loader2, Trash2, Pencil, RefreshCw, Users, Search, Phone, Clock, MessageSquare, Send, X } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PredictionBadge, StatusBadge } from "@/components/badges";
import VoterEditDialog from "@/components/VoterEditDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const ALL = "__all__";

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleString("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function waitDays(v, threshold = 3) {
  if ((v.interested || []).length > 0) return 0;
  const d = Math.floor((Date.now() - new Date(v.added_at).getTime()) / 86400000);
  return d >= threshold ? d : 0;
}

export default function Pool() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [committees, setCommittees] = useState([]);
  const [team, setTeam] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [editId, setEditId] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [claimTarget, setClaimTarget] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [onlyUnseen, setOnlyUnseen] = useState(false);
  const [prevSeen] = useState(() => { try { return localStorage.getItem(`imeak_pool_seen_${user.id}`) || ""; } catch { return ""; } });
  const [waitThreshold, setWaitThreshold] = useState(() => { try { return Number(localStorage.getItem("imeak_pool_wait_threshold")) || 3; } catch { return 3; } });
  const [sortBy, setSortBy] = useState("newest");
  useEffect(() => { try { localStorage.setItem("imeak_pool_wait_threshold", String(waitThreshold)); } catch { /* ignore */ } }, [waitThreshold]);
  const [fCommittee, setFCommittee] = useState(ALL);
  const [fBranch, setFBranch] = useState(ALL);
  const [q, setQ] = useState("");

  const canRemove = (p) => user.is_owner || user.role === "super_admin" || p.added_by === user.id;
  const committeeName = (no) => (committees.find((c) => c.committee_no === no) || {}).name || "";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/pool");
      setRows(data);
      try {
        localStorage.setItem(`imeak_pool_seen_${user.id}`, new Date().toISOString());
        window.dispatchEvent(new Event("pool-seen"));
      } catch { /* ignore */ }
    } catch (e) {
      toast.error("Havuz yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/committees").then(({ data }) => setCommittees(data)).catch(() => {});
    api.get("/meta").then(({ data }) => { setTeam(data.team_members || []); if (data.sectors) setSectors(data.sectors); }).catch(() => {});
  }, []);

  const toggleInterest = async (v) => {
    const mine = (v.interested || []).some((x) => x.user_id === user.id);
    setBusyId(v.id);
    try {
      const { data } = await api.post(`/voters/${v.id}/pool/interest`, { interested: !mine });
      setRows((prev) => prev.map((r) => (r.id === v.id ? { ...r, interested: data.interested } : r)));
      toast.success(mine ? "İlgilenme işareti kaldırıldı" : "Bu firmayla ilgilendiğiniz işaretlendi");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const [commentDraft, setCommentDraft] = useState({});
  const [commentBusy, setCommentBusy] = useState(null);

  const addComment = async (v) => {
    const text = (commentDraft[v.id] || "").trim();
    if (!text) return;
    setCommentBusy(v.id);
    try {
      const { data } = await api.post(`/voters/${v.id}/pool/comment`, { text });
      setRows((prev) => prev.map((r) => (r.id === v.id ? { ...r, comments: [...(r.comments || []), data] } : r)));
      setCommentDraft((d) => ({ ...d, [v.id]: "" }));
      toast.success("Yorum eklendi, ilgililere bildirildi");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCommentBusy(null);
    }
  };

  const deleteComment = async (v, c) => {
    setCommentBusy(v.id);
    try {
      await api.delete(`/voters/${v.id}/pool/comment/${c.id}`);
      setRows((prev) => prev.map((r) => (r.id === v.id ? { ...r, comments: (r.comments || []).filter((x) => x.id !== c.id) } : r)));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCommentBusy(null);
    }
  };

  const removeFromPool = async (v) => {    setBusyId(v.id);
    try {
      await api.delete(`/voters/${v.id}/pool`);
      setRows((prev) => prev.filter((r) => r.id !== v.id));
      toast.success(`${v.company_name} havuzdan çıkarıldı`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusyId(null);
    }
  };

  const doClaim = async () => {
    if (!claimTarget) return;
    setClaiming(true);
    try {
      const { data } = await api.post(`/voters/${claimTarget.id}/pool/claim`);
      setRows((prev) => prev.filter((r) => r.id !== claimTarget.id));
      toast.success(`${claimTarget.company_name} devralındı — sorumlu: ${data.responsible}`);
      setClaimTarget(null);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setClaiming(false);
    }
  };

  const committeeOpts = React.useMemo(() => [...new Set(rows.map((r) => r.committee_no))].sort((a, b) => a - b), [rows]);
  const branchOpts = React.useMemo(() => [...new Set(rows.map((r) => r.branch).filter(Boolean))].sort((a, b) => a.localeCompare(b, "tr")), [rows]);
  const filtered = React.useMemo(() => {
    const term = q.trim().toLocaleLowerCase("tr");
    return rows.filter((r) => {
      if (onlyUnseen && !((!prevSeen || (r.added_at || "") > prevSeen) && r.added_by !== user.id)) return false;
      if (fCommittee !== ALL && r.committee_no !== Number(fCommittee)) return false;
      if (fBranch !== ALL && r.branch !== fBranch) return false;
      if (term) {
        const hay = `${r.company_name || ""} ${r.contact_person || ""} ${r.added_by_name || ""} ${r.note || ""} ${(r.comments || []).map((c) => `${c.name} ${c.text}`).join(" ")}`.toLocaleLowerCase("tr");
        if (!hay.includes(term)) return false;
      }
      return true;
    });
  }, [rows, fCommittee, fBranch, q, onlyUnseen, prevSeen, user.id]);
  const unseenCount = React.useMemo(() => rows.filter((r) => (!prevSeen || (r.added_at || "") > prevSeen) && r.added_by !== user.id).length, [rows, prevSeen, user.id]);
  const sorted = React.useMemo(() => {
    const arr = [...filtered];
    if (sortBy === "waiting") arr.sort((a, b) => (a.added_at || "").localeCompare(b.added_at || ""));
    else if (sortBy === "interested") arr.sort((a, b) => ((b.interested || []).length - (a.interested || []).length) || (b.added_at || "").localeCompare(a.added_at || ""));
    else arr.sort((a, b) => (b.added_at || "").localeCompare(a.added_at || ""));
    return arr;
  }, [filtered, sortBy]);

  return (
    <div className="space-y-4" data-testid="pool-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-foreground flex items-center gap-2">
            <LifeBuoy className="h-6 w-6 text-cyan-600" /> Havuz
          </h1>
          <p className="text-sm text-muted-foreground">
            Ulaşamadığınız / tanımadığınız firmaları havuza atın; tanıyan biri "ilgileniyorum" diyip devralabilir. {rows.length} firma havuzda{filtered.length !== rows.length ? ` · ${filtered.length} gösteriliyor` : ""}.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="pool-refresh">
          <RefreshCw className="h-4 w-4 mr-2" />Yenile
        </Button>
      </div>

      {rows.length > 0 && (
        <Card className="p-3 border border-border" data-testid="pool-filters">
          <div className="flex flex-col md:flex-row md:flex-wrap gap-2">
            <div className="relative flex-1 min-w-[180px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Firma, kişi, ekleyen veya not ara..." className="pl-9" data-testid="pool-search" />
            </div>
            <Select value={fCommittee} onValueChange={setFCommittee}>
              <SelectTrigger className="md:w-52" data-testid="pool-filter-committee"><SelectValue placeholder="Komite" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value={ALL}>Tüm Komiteler</SelectItem>
                {committeeOpts.map((no) => <SelectItem key={no} value={String(no)}>#{no}{committeeName(no) ? ` ${committeeName(no)}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={fBranch} onValueChange={setFBranch}>
              <SelectTrigger className="md:w-44" data-testid="pool-filter-branch"><SelectValue placeholder="Şube" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value={ALL}>Tüm Şubeler</SelectItem>
                {branchOpts.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="md:w-52" data-testid="pool-sort"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="newest">En yeni önce</SelectItem>
                <SelectItem value="waiting">En uzun bekleyen önce</SelectItem>
                <SelectItem value="interested">En çok ilgilenilen önce</SelectItem>
              </SelectContent>
            </Select>
            <Select value={String(waitThreshold)} onValueChange={(val) => setWaitThreshold(Number(val))}>
              <SelectTrigger className="md:w-40" data-testid="pool-wait-threshold"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="3">Uyarı eşiği: 3 gün</SelectItem>
                <SelectItem value="5">Uyarı eşiği: 5 gün</SelectItem>
                <SelectItem value="7">Uyarı eşiği: 7 gün</SelectItem>
              </SelectContent>
            </Select>
            <Button variant={onlyUnseen ? "default" : "outline"} className={onlyUnseen ? "bg-cyan-600 hover:bg-cyan-600/90" : ""} onClick={() => setOnlyUnseen((v) => !v)} data-testid="pool-filter-unseen">
              Görmediklerim{unseenCount ? ` (${unseenCount})` : ""}
            </Button>
            {(fCommittee !== ALL || fBranch !== ALL || q || onlyUnseen) && (
              <Button variant="ghost" size="sm" onClick={() => { setFCommittee(ALL); setFBranch(ALL); setQ(""); setOnlyUnseen(false); }} data-testid="pool-filter-clear">Temizle</Button>
            )}
          </div>
        </Card>
      )}

      {loading ? (
        <div className="space-y-2">{[...Array(5)].map((_, i) => <Skeleton key={i} className="h-24 rounded-lg" />)}</div>
      ) : rows.length === 0 ? (
        <Card className="p-12 border border-dashed border-border text-center" data-testid="pool-empty">
          <LifeBuoy className="h-10 w-10 mx-auto text-muted-foreground/50 mb-3" />
          <p className="text-muted-foreground">Havuz boş. Bir firmayı düzenleme kartından "Havuza At" ile ekleyebilirsiniz.</p>
        </Card>
      ) : filtered.length === 0 ? (
        <Card className="p-12 border border-dashed border-border text-center" data-testid="pool-no-match">
          <Search className="h-8 w-8 mx-auto text-muted-foreground/50 mb-3" />
          <p className="text-muted-foreground">Filtreye uyan firma yok.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="pool-list">
          {sorted.map((v) => {
            const mine = (v.interested || []).some((x) => x.user_id === user.id);
            const wd = waitDays(v, waitThreshold);
            return (
              <Card key={v.id} className="p-4 border border-border flex flex-col gap-3" data-testid={`pool-card-${v.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <button type="button" onClick={() => setEditId(v.id)} className="font-medium text-foreground hover:text-cyan-600 transition-colors text-left truncate block w-full" data-testid={`pool-name-${v.id}`}>
                      {v.company_name}
                    </button>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      #{v.committee_no}{committeeName(v.committee_no) ? ` ${committeeName(v.committee_no)}` : ""} &bull; {v.branch}
                      {v.contact_person ? ` \u2022 ${v.contact_person}` : ""}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <StatusBadge value={v.status} />
                    <PredictionBadge value={v.prediction} />
                  </div>
                </div>

                {v.note && (
                  <p className="text-sm bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900 rounded-md px-3 py-2 text-amber-800 dark:text-amber-300" data-testid={`pool-note-${v.id}`}>
                    "{v.note}"
                  </p>
                )}

                {wd > 0 && (
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold text-red-600 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 rounded px-2 py-1 w-fit" data-testid={`pool-waiting-${v.id}`}>
                    <Clock className="h-3.5 w-3.5" /> {wd} gündür bekliyor, kimse ilgilenmedi
                  </div>
                )}

                <div className="flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
                  <span data-testid={`pool-meta-${v.id}`}>Ekleyen: <b className="text-foreground/80">{v.added_by_name}</b> &bull; {fmtDate(v.added_at)}</span>
                  {v.team_assignment_2026 && <span className="truncate shrink-0" data-testid={`pool-owner-${v.id}`}>Sorumlu: <b className="text-foreground/80">{v.team_assignment_2026}</b></span>}
                </div>

                {(v.interested || []).length > 0 && (
                  <div className="flex items-center gap-1.5 flex-wrap" data-testid={`pool-interested-${v.id}`}>
                    <Users className="h-3.5 w-3.5 text-emerald-600" />
                    {(v.interested || []).map((x) => (
                      <Badge key={x.user_id} variant="secondary" className="text-[10px] bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 hover:bg-emerald-100">
                        {x.name}
                      </Badge>
                    ))}
                  </div>
                )}

                <div className="border-t border-border pt-2" data-testid={`pool-comments-${v.id}`}>
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <MessageSquare className="h-3.5 w-3.5 text-cyan-600" />
                    <span className="text-[11px] font-semibold text-muted-foreground">
                      Yorumlar{(v.comments || []).length ? ` (${(v.comments || []).length})` : ""}
                    </span>
                    {(v.comments || []).some((c) => c.user_id !== user.id && (!prevSeen || (c.at || "") > prevSeen)) && (
                      <Badge className="text-[9px] bg-cyan-600 hover:bg-cyan-600" data-testid={`pool-comment-new-${v.id}`}>yeni</Badge>
                    )}
                  </div>
                  <div className="space-y-1.5">
                    {(v.comments || []).map((c) => (
                      <div key={c.id} className="group flex items-start gap-1.5 text-xs bg-muted/50 rounded-md px-2 py-1.5" data-testid={`pool-comment-${c.id}`}>
                        <p className="flex-1 min-w-0 break-words">
                          <b className="text-foreground/80">{c.name}</b>
                          <span className="text-muted-foreground"> · {fmtDate(c.at)}</span>
                          <br />{c.text}
                        </p>
                        {(c.user_id === user.id || user.role === "super_admin") && (
                          <button type="button" className="opacity-40 hover:opacity-100 text-destructive shrink-0"
                            disabled={commentBusy === v.id} onClick={() => deleteComment(v, c)}
                            data-testid={`pool-comment-delete-${c.id}`} title="Yorumu sil">
                            <X className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <Input
                      value={commentDraft[v.id] || ""}
                      onChange={(e) => setCommentDraft((d) => ({ ...d, [v.id]: e.target.value }))}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addComment(v); } }}
                      placeholder="Bilgi/ipucu yaz: ör. Falanca kişi bu firmanın ortağı…"
                      className="h-8 text-xs"
                      data-testid={`pool-comment-input-${v.id}`}
                    />
                    <Button size="sm" variant="outline" className="h-8 px-2" disabled={commentBusy === v.id || !(commentDraft[v.id] || "").trim()}
                      onClick={() => addComment(v)} data-testid={`pool-comment-send-${v.id}`}>
                      {commentBusy === v.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                    </Button>
                  </div>
                </div>

                <div className="flex items-center gap-2 mt-auto pt-1">
                  <Button size="sm" className={mine ? "bg-emerald-600 hover:bg-emerald-700 flex-1" : "flex-1 bg-[#0F2C59] hover:bg-[#0F2C59]/90"} disabled={busyId === v.id} onClick={() => toggleInterest(v)} data-testid={`pool-interest-${v.id}`}>
                    {busyId === v.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Hand className="h-4 w-4 mr-1.5" />{mine ? "İlgileniyorum ✓" : "Ben İlgileniyorum"}</>}
                  </Button>
                  <Button size="sm" variant="outline" className="border-emerald-500 text-emerald-700 dark:text-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/40" onClick={() => setClaimTarget(v)} data-testid={`pool-claim-${v.id}`}>
                    Devral
                  </Button>
                  {v.phone && (
                    <Button size="sm" variant="outline" asChild className="text-emerald-700 dark:text-emerald-300" title={`Ara: ${v.phone}`}>
                      <a href={`tel:${v.phone.replace(/\s/g, "")}`} data-testid={`pool-call-${v.id}`}><Phone className="h-4 w-4" /></a>
                    </Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => setEditId(v.id)} data-testid={`pool-edit-${v.id}`}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  {canRemove(v) && (
                    <Button size="sm" variant="ghost" className="text-destructive" disabled={busyId === v.id} onClick={() => removeFromPool(v)} data-testid={`pool-remove-${v.id}`} title="Havuzdan çıkar">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <VoterEditDialog voterId={editId} open={!!editId} onClose={() => setEditId(null)} onSaved={() => load()} team={team} sectors={sectors} />

      <AlertDialog open={!!claimTarget} onOpenChange={(o) => !o && setClaimTarget(null)}>
        <AlertDialogContent data-testid="pool-claim-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Firmayı devral?</AlertDialogTitle>
            <AlertDialogDescription>
              <b>{claimTarget?.company_name}</b> sizin sorumluluğunuza atanacak ve havuzdan çıkarılacak. Bu, firmanın 2026 sorumlusunu değiştirir. Onaylıyor musunuz?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="pool-claim-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction className="bg-[#0F2C59] hover:bg-[#0F2C59]/90" disabled={claiming} onClick={doClaim} data-testid="pool-claim-confirm">
              {claiming ? <Loader2 className="h-4 w-4 animate-spin" /> : "Devral"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
